import { toast } from '@aus-platform/design-system';
import { fromBlob } from 'geotiff';
import { isArray, isEmpty, isNil } from 'lodash';
import { v4 as uuid4 } from 'uuid';
import { FileType } from '../api';
import { FileExtension, UploadFilesErrorMessages } from '../enums';
import type { SelectedFile } from '../types';

// Types.
export type GeoTIFFValidationResult = {
  error: string;
  warning: string;
  info: string;
  isValid: boolean;
};

// Add more executable or archive MIME types as needed.
const BLACKLISTED_MIME_TYPES = [
  'application/x-msdownload',
  'application/x-sh',
  'application/x-tar',
  'application/x-gzip',
  'application/x-rpm',
  'application/x-deb',
  'application/octet-stream',
  'application/x-msi',
  'application/x-msdos-program',
  'application/x-msdos-windows',
  'text/vbscript',
  'application/x-scr',
  'application/vnd.android.package-archive',
  'application/x-apple-diskimage',
];

// Executable extensions.
const BLACKLISTED_EXTENSIONS = [
  'exe',
  'bat',
  'sh',
  'apk',
  'msi',
  'jar',
  'dmg',
  'app',
  'cmd',
  'com',
  'vbs',
  'scr',
  'pkg',
];
const SUPPORTED_SAME_SIGNATURE_EXTENSIONS = ['xls', 'xlsx', 'ppt', 'pptx'];

const GENERIC_BINARY_MIME_TYPE = 'application/octet-stream';

export type FileSafetyOptions = {
  // Extensions whose generic binary label is accepted. Content and extension checks still apply.
  allowedBinaryExtensions?: string[];
};

const magicNumberMap = {
  '4d5a': 'application/x-msdownload',
  d0cf11e0: 'application/x-msi',
  '1f8b0800': 'application/x-gzip',
  '75737461': 'application/x-tar',
  edabeedb: 'application/x-rpm',
  '213c61': 'application/x-deb',
  '7f454c46': 'application/octet-stream',
  '4e45': 'application/x-msdos-program',
  '78 61 72 64': 'application/x-apple-diskimage',
};

const MAX_FILE_SIZE = 2 * 1000 * 1000 * 1000;
// 200 GB - Warning limit for Ortho files.
const ORTHO_FILE_SIZE_WARNING_LIMIT = 200 * 1000 * 1000 * 1000;

// Enums.
export enum GeoTIFFStatusMessage {
  MissingCRS = 'Missing CRS information. Please upload a file with a valid coordinate reference system.',
  MissingBandInfo = 'Unable to detect the number of bands in the GeoTIFF.',
  LargeFile = 'This file is larger than 200 GB. Uploads may take longer, but you can still proceed.',
  InvalidFile = 'The selected file is not a valid .tif/.tiff file.',
  InvalidFileExtension = 'Please select a file with a .tif/.tiff extension.',

  // Ortho Validation Messages.
  MoreThan4Bands = 'This Ortho file contains more than 4 bands. We currently support GeoTIFFs with 1 to 4 bands only.',
  OneBand = 'This GeoTIFF contains only 1 band and will be visualized in Grey Scale.',
  TwoBands = 'This GeoTIFF contains 2 bands.',
  ThreeBands = 'This GeoTIFF contains 3 bands.',
  FourBands = 'This GeoTIFF contains 4 bands.',
  OrthoMetaValidationFailed = 'Meta validation failed. Please check the CRS and number of bands in your Ortho file.',

  // DEM Validation Messages.
  DEMInvalidBandCount = 'The DEM file should contain exactly 1 band.',
  DEMMetaValidationFailed = 'Meta validation failed. Please check the CRS and number of bands in your DEM file.',
}

/**
 * Return a validation message for an orthomosaic file
 * based on the number of bands detected in the file.
 *
 * @param {number} bandCount - Number of bands detected in the GeoTIFF.
 * @returns {string} One of the values from `OrthoValidationMessage`.
 *   - For bandCount 1..4 returns the corresponding message.
 */
export const getOrthoBandValidationMessage = (bandCount: number): string => {
  switch (bandCount) {
    case 1:
      return GeoTIFFStatusMessage.OneBand;
    case 2:
      return GeoTIFFStatusMessage.TwoBands;
    case 3:
      return GeoTIFFStatusMessage.ThreeBands;
    case 4:
      return GeoTIFFStatusMessage.FourBands;
  }
  return '';
};

/**
 * Validates whether the file has a supported GeoTIFF extension.
 *
 * Checks if the file extension is either `.tif` or `.tiff`.
 * Returns a validation result object with an error message if the extension is invalid.
 *
 * @param file - The file to validate.
 * @returns Validation result if file has Invalid Extension.
 */
const validateGeoTIFFExtension = (file: File) => {
  const hasValidExtension = FileValidator.checkExtension(file, [
    FileExtension.GeoTIFF,
    FileExtension.TIFF,
  ]);

  if (!hasValidExtension) {
    return {
      error: GeoTIFFStatusMessage.InvalidFileExtension,
      warning: '',
      info: '',
      isValid: false,
    };
  }
};

/**
 * Validates a GeoTIFF/TIFF file by checking band count, and CRS.
 *
 * @param file - The input file.
 * @returns Band count and CRS if exists.
 * @throws Error If the file is Invalid.
 */
const extractGeoTIFFMetadata = async (
  file: File,
): Promise<{ bandCount: number; isCrsExist: boolean }> => {
  const tiff = await fromBlob(file);
  const image = await tiff.getImage();

  const bandCount = image.getSamplesPerPixel();
  const crs = image.getGeoKeys();

  return { bandCount, isCrsExist: !isEmpty(crs) };
};

/**
 * Validates band count, CRS, and file size for GeoTIFFs (Orthomosaic or DEM).
 *
 * @param isCrsExist - Whether CRS metadata exists.
 * @param bandCount - Number of bands.
 * @param fileType - File type (Orthomosaic or DEM).
 * @param file - The file to validate.
 * @returns Validation result or null if file is valid.
 */
const validateCrsAndBandInfo = (
  isCrsExist: boolean,
  bandCount: number | null | undefined,
  fileType: FileType,
  file: File,
): GeoTIFFValidationResult | null => {
  const isOrthomosaicFile = fileType === FileType.Orthomosaic;
  const isDEMFile = fileType === FileType.CapturedDsm;

  // Band Count Validation.
  if (isNil(bandCount) || bandCount === 0) {
    return {
      error: GeoTIFFStatusMessage.MissingBandInfo,
      warning: '',
      info: '',
      isValid: false,
    };
  }

  if (isOrthomosaicFile && bandCount > 4 && !isCrsExist) {
    return {
      error: GeoTIFFStatusMessage.OrthoMetaValidationFailed,
      warning: '',
      info: '',
      isValid: false,
    };
  }

  if (isDEMFile && bandCount !== 1 && !isCrsExist) {
    return {
      error: GeoTIFFStatusMessage.DEMMetaValidationFailed,
      warning: '',
      info: '',
      isValid: false,
    };
  }

  if (isOrthomosaicFile && bandCount > 4) {
    return {
      error: GeoTIFFStatusMessage.MoreThan4Bands,
      warning: '',
      info: '',
      isValid: false,
    };
  }

  if (isDEMFile && bandCount !== 1) {
    return {
      error: GeoTIFFStatusMessage.DEMInvalidBandCount,
      warning: '',
      info: '',
      isValid: false,
    };
  }

  // Checking CRS existence.
  if (!isCrsExist) {
    return {
      error: GeoTIFFStatusMessage.MissingCRS,
      warning: '',
      info: '',
      isValid: false,
    };
  }

  // File Size Warning.
  if (file.size > ORTHO_FILE_SIZE_WARNING_LIMIT) {
    return {
      error: '',
      warning: GeoTIFFStatusMessage.LargeFile,
      info: '',
      isValid: true,
    };
  }

  return null;
};

export const FileValidator = {
  checkExtension: (
    file: File,
    extensions: FileExtension | FileExtension[],
  ): boolean => {
    const allowedExtensions = isArray(extensions) ? extensions : [extensions];
    // Get file extension and prepend dot to match enum (e.g., '.tif').
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    return allowedExtensions.some(
      (extension: FileExtension) => extension.toLowerCase() === fileExtension,
    );
  },

  validateOrtho: async (file: File): Promise<GeoTIFFValidationResult> => {
    try {
      const extensionValidation = validateGeoTIFFExtension(file);
      if (extensionValidation) return extensionValidation;

      const { bandCount, isCrsExist } = await extractGeoTIFFMetadata(file);
      const crsAndBandValidation = validateCrsAndBandInfo(
        isCrsExist,
        bandCount,
        FileType.Orthomosaic,
        file,
      );

      if (crsAndBandValidation) return crsAndBandValidation;

      // Band Count Info Message.
      const bandCountInfoMessage = getOrthoBandValidationMessage(bandCount);

      return {
        error: '',
        warning: '',
        info: bandCountInfoMessage,
        isValid: true,
      };
    } catch (error) {
      console.error(error);

      // Fallback to ensure consistent error message on validation failure.
      return {
        error: GeoTIFFStatusMessage.InvalidFile,
        warning: '',
        info: '',
        isValid: false,
      };
    }
  },

  validateDEM: async (file: File): Promise<GeoTIFFValidationResult> => {
    try {
      const extensionValidation = validateGeoTIFFExtension(file);
      if (extensionValidation) return extensionValidation;

      const { bandCount, isCrsExist } = await extractGeoTIFFMetadata(file);
      const crsAndBandValidation = validateCrsAndBandInfo(
        isCrsExist,
        bandCount,
        FileType.CapturedDsm,
        file,
      );

      if (crsAndBandValidation) return crsAndBandValidation;

      return {
        error: '',
        warning: '',
        info: '',
        isValid: true,
      };
    } catch (error) {
      console.error(error);

      // Fallback to ensure consistent error message on validation failure.
      return {
        error: GeoTIFFStatusMessage.InvalidFile,
        warning: '',
        info: '',
        isValid: false,
      };
    }
  },
};

/**
 * Detects the MIME type of a file by reading the first 4 bytes (magic number).
 * Resolves to the MIME type if found, or 'unknown' if not.
 * @param {File} file - The file to check.
 * @returns {Promise<string>} Resolves with the MIME type or 'unknown' if no match is found.
 */
const detectMimeTypeFromMagicNumber = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    if (!file) {
      return reject('File is undefined or null');
    }
    const reader = new FileReader();

    reader.onloadend = () => {
      const buffer = new Uint8Array(reader.result as ArrayBuffer);
      const magicBytes = buffer.slice(0, 4);

      const hexString = Array.from(magicBytes)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');

      const mimeType = magicNumberMap[hexString] || 'unknown';

      resolve(mimeType);
    };

    reader.onerror = () => {
      reject('Error reading file');
    };

    reader.readAsArrayBuffer(file.slice(0, 4));
  });
};

/**
 * Returns a filename extension without its leading period, in lower case.
 * @param extension - Extension or filename suffix to normalize.
 * @returns The normalized extension.
 */
const normalizeFileExtension = (extension: string): string => {
  return extension.replace(/^\./, '').toLowerCase();
};

/**
 * Determines the MIME type of a file based on its magic number or extension.
 * @param {File} file - The file to check.
 * @param {FileSafetyOptions} options - Extensions whose generic binary label is accepted.
 * @returns {Promise<string>} The detected MIME type or 'None' if undetectable.
 */
const getFileMimeType = async (
  file: File,
  { allowedBinaryExtensions = [] }: FileSafetyOptions = {},
): Promise<string> => {
  try {
    let mimeType = await detectMimeTypeFromMagicNumber(file);

    // If magic number fails, use file's MIME type (based on extension).
    if (mimeType === 'unknown') {
      const fileExtension = normalizeFileExtension(
        file.name.split('.').pop() || '',
      );
      const isAllowedBinaryExtension =
        file.type === GENERIC_BINARY_MIME_TYPE &&
        allowedBinaryExtensions.some(
          (extension) => normalizeFileExtension(extension) === fileExtension,
        );
      mimeType = (!isAllowedBinaryExtension && file.type) || 'None';
    }
    return mimeType;
  } catch (error) {
    console.error('Error detecting MIME type from magic number:', error);
    return 'None';
  }
};

/**
 * Detects executable-like files (magic number, MIME, extension) and blocks them.
 * @param {File} file - The file to check.
 * @param {boolean} raiseToast - When true (default), shows a blacklist toast if the file is unsafe.
 * @param {FileSafetyOptions} options - Extensions whose generic binary label is accepted.
 * @returns {Promise<boolean>} - Resolves to `true` if the file is safe, `false` otherwise.
 */
export const isFileSafe = async (
  file: File,
  raiseToast = true,
  options: FileSafetyOptions = {},
): Promise<boolean> => {
  const mimeType = await getFileMimeType(file, options);
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

  const isBlocked =
    (BLACKLISTED_MIME_TYPES.includes(mimeType) &&
      !SUPPORTED_SAME_SIGNATURE_EXTENSIONS.includes(fileExtension)) ||
    BLACKLISTED_EXTENSIONS.includes(fileExtension);

  if (isBlocked) {
    if (raiseToast) {
      toast.error(UploadFilesErrorMessages.BlacklistedFileFormat);
    }

    return false;
  }
  return true;
};

export const ensureBatchFilesNotExecutable = async (
  files: File[],
): Promise<boolean> => {
  const safeFlags = await Promise.all(
    files.map((file) => isFileSafe(file, false)),
  );

  if (!safeFlags.every(Boolean)) {
    toast.error(UploadFilesErrorMessages.BlacklistedFileFormat);
    return false;
  }
  return true;
};

export const processStoreFiles = async (
  files: FileList | null,
  selectedFiles: SelectedFile[],
  validateFileSize = true,
): Promise<SelectedFile[]> => {
  let anyBlacklisted = false;
  const processedFiles = await Promise.all(
    Array.from(files || []).map(async (newFile) => {
      // First check if file already exists.
      const isDuplicate = selectedFiles.some(
        ({ file }) => file.name === newFile.name,
      );
      if (isDuplicate) {
        return null;
      }

      let isFileSizeTooBig = false;

      // Check if the file size is too large.
      if (validateFileSize) {
        isFileSizeTooBig = newFile.size > MAX_FILE_SIZE;

        if (isFileSizeTooBig) {
          toast.error(UploadFilesErrorMessages.FileTooLarge);
        }
      }

      const isAllowedFile = await isFileSafe(newFile, false);

      if (!isAllowedFile) {
        anyBlacklisted = true;
      }

      if (!isFileSizeTooBig && isAllowedFile) {
        return { file: newFile, id: uuid4() };
      }

      return null;
    }),
  );

  if (anyBlacklisted) {
    toast.error(UploadFilesErrorMessages.BlacklistedFileFormat);
  }

  return processedFiles.filter((file) => file !== null);
};
